import {
  createInput,
  getInputList,
  refetchOrders,
  searchOrders,
  validateLot,
} from "src/apis/pda1122Api";
import { useToast } from "src/hooks/useToast";
import {
  PDA1122_R01InputItem,
  PDA1122_R01OrderDto,
  PDA1122_R01SearchParams,
} from "./PDA1122_R01Dto";
import usePDA1122_R01Store from "./usePDA1122_R01Store";

type OrderResponse = { data?: { data?: PDA1122_R01OrderDto[] } };
type ApiError = { response?: { data?: { message?: string } } };

export const usePDA1122_R01Content = () => {
  const store = usePDA1122_R01Store();
  const { success, error, warning } = useToast();

  const buildParams = (): PDA1122_R01SearchParams => {
    const params: PDA1122_R01SearchParams = {};
    if (store.searchParams.productOrderNo) {
      params.productOrderNo = store.searchParams.productOrderNo;
    }
    if (store.searchParams.startDate) {
      params.startDate = store.searchParams.startDate.split("T")[0];
    }
    if (store.searchParams.endDate) {
      params.endDate = store.searchParams.endDate.split("T")[0];
    }
    return params;
  };

  const fetchOrders = async (
    fetcher: (params: PDA1122_R01SearchParams) => Promise<unknown>
  ) => {
    store.setLoading(true);
    try {
      const response = (await fetcher(buildParams())) as OrderResponse;
      const orders = response.data?.data || [];
      store.setOrderList(orders);
      if (orders.length === 0) warning("조회된 작업지시가 없습니다.");
    } catch (fetchError: unknown) {
      const message =
        (fetchError as ApiError).response?.data?.message ||
        "작업지시 조회 중 오류가 발생했습니다.";
      error(message);
      store.setOrderList([]);
    } finally {
      store.setLoading(false);
    }
  };

  const handleSearch = () => fetchOrders(searchOrders);
  const handleRefetch = () => fetchOrders(refetchOrders);

  const loadSavedInputs = async (productOrderNo: string) => {
    try {
      const response = await getInputList(productOrderNo);
      const savedInputs = response?.data?.data || [];
      store.setSavedInputList(savedInputs.map((item) => ({ ...item, saved: true })));
    } catch {
      store.setSavedInputList([]);
    }
  };

  const handleSelectOrder = async (order: PDA1122_R01OrderDto) => {
    if (!order.productOrderNo) return;
    store.setLoading(true);
    store.setSelectedOrder(order);
    store.setScanResult(null);
    store.setLotInput("");
    store.clearInputs();
    store.setSavedInputList([]);
    try {
      await loadSavedInputs(order.productOrderNo);
      store.setShowList(false);
    } finally {
      store.setLoading(false);
    }
  };

  const handleBackToList = () => {
    if (store.inputList.length > 0) {
      const confirmed = window.confirm(
        `투입 대기중인 ${store.inputList.length}건이 있습니다.\n저장하지 않고 나가시겠습니까?`
      );
      if (!confirmed) return;
    }
    store.resetDetail();
    store.setShowList(true);
  };

  const handleScanLot = async (scanValue?: string) => {
    const lotNo = (scanValue ?? store.lotInput).trim();
    if (!lotNo) {
      warning("LOT 번호를 스캔하세요.");
      return;
    }
    if (!store.selectedOrder) return;

    store.setLoading(true);
    try {
      const response = await validateLot(store.selectedOrder.productOrderNo, lotNo);
      const scan = response.data?.data;
      if (!scan) throw new Error("LOT 정보를 찾을 수 없습니다.");
      const currentInputList = usePDA1122_R01Store.getState().inputList;
      if (
        scan.previousLotNo &&
        !currentInputList.some((item) => item.lotNo === scan.previousLotNo)
      ) {
        warning(`선행 LOT(${scan.previousLotNo})을 먼저 투입하세요.`);
        store.setScanResult(null);
        return;
      }
      store.setScanResult(scan);
      success(`${scan.itemNm} (재고 ${scan.lotQty.toLocaleString()})`);
    } catch {
      store.setScanResult(null);
    } finally {
      store.setLotInput("");
      store.setLoading(false);
    }
  };

  const handleInput = () => {
    if (!store.scanResult) {
      warning("LOT를 먼저 스캔하세요.");
      return;
    }
    if (
      store.scanResult.previousLotNo &&
      !store.inputList.some((item) => item.lotNo === store.scanResult?.previousLotNo)
    ) {
      warning(`선행 LOT(${store.scanResult.previousLotNo})을 먼저 투입하세요.`);
      store.setScanResult(null);
      return;
    }
    const added = store.addInput({
      itemCd: store.scanResult.itemCd,
      itemNm: store.scanResult.itemNm,
      itemSpec: store.scanResult.itemSpec,
      lotNo: store.scanResult.lotNo,
      lotQty: store.scanResult.lotQty,
      lotCreateDt: store.scanResult.lotCreateDt,
      previousLotNo: store.scanResult.previousLotNo,
      saved: false,
    });
    if (!added) {
      warning("이미 투입 목록에 있는 LOT입니다.");
      return;
    }
    success(`투입 추가 (${store.inputList.length + 1}건)`);
    store.setScanResult(null);
    store.setLotInput("");
  };

  const handleRemoveInput = (inputItem: PDA1122_R01InputItem) => {
    const laterInput = store.inputList.find(
      (item) => item.previousLotNo === inputItem.lotNo
    );
    if (laterInput) {
      warning(
        `후순위 LOT(${laterInput.lotNo})이 있어 선행 LOT(${inputItem.lotNo})을 제거할 수 없습니다.`
      );
      return;
    }
    store.removeInput(inputItem.lotNo);
  };

  const handleComplete = async () => {
    if (!store.selectedOrder) return;
    if (store.inputList.length === 0) {
      warning("투입할 LOT가 없습니다.");
      return;
    }
    const pendingLotNoSet = new Set(store.inputList.map((item) => item.lotNo));
    const invalidInput = store.inputList.find(
      (item) => item.previousLotNo && !pendingLotNoSet.has(item.previousLotNo)
    );
    if (invalidInput?.previousLotNo) {
      warning(`선행 LOT(${invalidInput.previousLotNo})을 먼저 투입하세요.`);
      return;
    }

    store.setLoading(true);
    try {
      await createInput({
        productOrderNo: store.selectedOrder.productOrderNo,
        lotList: store.inputList.map(({ itemCd, lotNo, lotQty }) => ({ itemCd, lotNo, lotQty })),
      });
      success(`자재 투입 완료 (LOT ${store.inputList.length}건)`);
      store.clearInputs();
      store.setScanResult(null);
      store.setLotInput("");
      await loadSavedInputs(store.selectedOrder.productOrderNo);
    } catch (completeError: unknown) {
      const message =
        (completeError as ApiError).response?.data?.message || "자재 투입에 실패했습니다.";
      error(message);
    } finally {
      store.setLoading(false);
    }
  };

  return {
    handleSearch,
    handleRefetch,
    handleSelectOrder,
    handleBackToList,
    handleScanLot,
    handleInput,
    handleRemoveInput,
    handleComplete,
  };
};
